import React, { useState, useEffect, useMemo } from "react";
import "../../assets/scss/miai-web/company-info-page.scss";
import MapView from "./MapView";
import Plot from 'react-plotly.js';
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LaunchIcon from "@mui/icons-material/Launch";

const CompanyInfoPage = ({ companyDetails, scoringResult, domainDetails }) => {
  const [selectedTopTab, setSelectedTopTab] = useState("about");
  const [selectedMiddleTab, setSelectedMiddleTab] = useState(null);
  const [middleTabs, setMiddleTabs] = useState([]);

  const topTabs = [
    { id: "about", label: "About Company" },
    { id: "product", label: "Product Details" },
    { id: "security", label: "Security & Compliance" },
    { id: "analyst", label: "Analyst Speak" },
    { id: "news", label: "Recent News" },
    { id: "similar", label: "Similar Vendors" },
  ];

  /** STATIC OPTIONS */
  const STATIC_MIDDLE_TABS = useMemo(() => ({
    about: ["Key Members", "Key Customers", "Key Partners", "Regional Presence", "Pricing"],
    security: ["Data Protection", "Access Control", "Compliance & Certs"],
    analyst: ["Management Consulting", "Tech Research", "Media & Academic", "Thought Leaders", "VC"],
    news: ["Product Related", "Partnerships & Customers", "Expansion & Hiring", "Media Coverage & Events"],
    similar: ["Competitors", "Alternatives"],
  }), []);

  /** ⬇️ DYNAMICALLY UPDATE PRODUCT MIDDLE TABS */
  useEffect(() => {
    if (selectedTopTab === "product") {
      const productList =
        companyDetails?.products?.map((p) => p.product_name) || [];

      setMiddleTabs(productList);
      setSelectedMiddleTab(null);
    } else {
      setMiddleTabs(STATIC_MIDDLE_TABS[selectedTopTab] || []);
      setSelectedMiddleTab(null);
    }
  }, [selectedTopTab, companyDetails, STATIC_MIDDLE_TABS]);

  //Radar chart
  const products = Array.isArray(scoringResult?.scored_structure?.products)
    ? scoringResult.scored_structure.products
    : [];

  // dynamic color generator
  const getColor = (index) => {
    const hue = (index * 137.508) % 360;
    return `hsl(${hue}, 65%, 45%)`;
  };

  const chartData = products
    .map((product, index) => {
      const values = product?.classification?.product_type?.values ?? {};

      const radarTheta = Object.keys(values);
      const radarR = Object.values(values);

      if (!radarTheta.length || !radarR.length) return null;

      return {
        type: "scatterpolar",
        r: radarR,
        theta: radarTheta,
        fill: "toself",
        name: product?.product_name?.value || `Product ${index + 1}`,
        line: {
          color: getColor(index),
          width: 2,
        },
        opacity: 0.6,
      };
    })
    .filter(Boolean);

  const chartLayout = {
    width: 550,
    height: 400,
    polar: {
      radialaxis: {
        visible: true,
        range: [0, 100]
      }
    },
    showlegend: false
  };

  // Security and compliance Graph
  const scoredProducts = Array.isArray(
    scoringResult?.scored_structure?.products,) ? scoringResult.scored_structure.products
    : [];

  const average = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

  const extractSecurityScores = (products, sectionKey) =>
    products.map((p) =>
      p?.security_governance?.[sectionKey]?.subsection_aggregate_score ??
      p?.security_features?.[sectionKey]?.subsection_aggregate_score,
    ).filter((v) => typeof v === "number");

  const dataProtectionScore = average(
    extractSecurityScores(scoredProducts, "data_protection"),
  );

  const accessControlScore = average(
    extractSecurityScores(scoredProducts, "access_controls"),
  );

  const complianceScore = average(
    extractSecurityScores(scoredProducts, "compliance_certifications"),
  );

  const isSecurityDataAvailable =
    (Number.isFinite(dataProtectionScore) && dataProtectionScore > 0) ||
    (Number.isFinite(accessControlScore) && accessControlScore > 0) ||
    (Number.isFinite(complianceScore) && complianceScore > 0);

  /* -------------------------------------------
      DEFAULT VIEW (Before selecting middle tab)
  ------------------------------------------- */
  const renderDefaultView = (section) => {
    switch (selectedTopTab) {
      case "about":
        return section === "left" ? (
          <div>
            <h5>Market Pluse</h5>
            <p>
              Evaluates overall health & growth by assessing performance,
              financial stability, market conditions and management strength.
            </p>

            <Plot
              data={[
                {
                  type: "bar",
                  orientation: "h",
                  y: ["Pedigree", "Brand", "Funding", "Recognition", "Growth"],
                  x: [1, 2, 3, 4],
                  marker: {
                    color: ["lightgreen", "#FFD580", "#FF5252", "lightblue"],
                  },
                },
              ]}
              layout={{
                width: 320,
                height: 240,
                margin: { t: 20, b: 20, l: 100, r: 20 },
                bargap: 0.4,

                xaxis: {
                  type: "linear",
                  tickformat: ",.0f",
                  separatethousands: true,
                  tickfont: { size: 11 },
                },

                yaxis: {
                  type: "category",
                  automargin: true,
                  tickfont: {
                    size: 10,
                    weight: "bold",
                  },
                },

                paper_bgcolor: "transparent",
                plot_bgcolor: "transparent",

                transition: {
                  duration: 800,
                  easing: "cubic-in-out",
                },
              }}
              config={{ displayModeBar: false }}
            />
          </div>
        ) : (
          <div>
            <h5>Use Cases handled by Harvey</h5>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
              <thead>
                <tr style={{ background: "#f5f5f5" }}>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>Product Name</th>
                  <th style={{ border: "1px solid #ddd", padding: "8px" }}>Domains</th>
                </tr>
              </thead>

              <tbody>
                {companyDetails?.products?.map((productItem, pIndex) => {
                  const domains = productItem?.classification?.domains || [];
                  return (
                    <tr key={pIndex}>
                      {/* Product Name */}
                      <td style={{ border: "1px solid #ddd", padding: "8px", fontWeight: "bold" }}>
                        {productItem?.product_name}
                      </td>

                      {/* Domains */}
                      <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                        {domains.length > 0 ? (
                          domains
                            .map((d) =>
                              d?.domain_name?.value
                                ? d.domain_name.value
                                : d?.domain_name   // fallback
                            )
                            .join(", ")  // comma separated
                        ) : (
                          "No domains"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        );

      case "product":
        return section === "left" ? (
          <div>
            <h3>Process Coverage</h3>
            <Plot
              data={[
                {
                  type: 'bar',
                  x: ["23", "24", "25"],
                  y: [126, 289, 231, 562],
                  marker: {
                    color: ['lightgreen', '#FFD580', '#FF5252', 'lightblue']
                  },
                },
              ]}
              layout={{
                borderRadius: '15%',
                overflow: 'hidden',
                height: 240,
                width: 320,
                xaxis: {
                  title: {
                    text: '',
                    font: {
                      size: 12,
                      weight: 'bold'
                    },
                    standoff: 10
                  },
                  type: 'category',
                  // tickangle: -45,
                  automargin: true,
                  tickfont: {
                    weight: 'bold',
                    size: 8
                  }
                },
                yaxis: {
                  title: {
                    text: "",
                    font: {
                      size: 12,
                      weight: 'bold'
                    },
                    standoff: 10
                  },
                  type: 'linear',
                  automargin: true,
                  tickfont: {
                    // weight: 'bold',
                    size: 12
                  },
                  tickformat: ',.0f',
                  separatethousands: true,
                  hoverformat: ',.0f'
                },
                title: {
                  text: "Revenue in $M",
                  x: 0.5,
                  xref: 'paper',
                  font: {
                    size: 12,
                    weight: 'bold'
                  }
                },
                margin: { t: 50, b: 20, l: 100, r: 10 },
                bargap: 0.5,
                scene: {
                  camera: {
                    eye: { x: 1.5, y: 1.5, z: 1.2 }
                  }
                },
                transition: {
                  duration: 1000,
                  easing: 'cubic-in-out'
                }
              }}
            />
            {/* <img src={imgsrc} style={{ width: "300px", height: "250px" }} /> */}


          </div>
        ) : (
          <div>
            <h3>Product Stats</h3>
            <Plot
              data={chartData}
              layout={chartLayout}
            />
          </div>
        );

      case "security":
        return section === "left" ? (
          <div>
            <h3>Security</h3>
            {/* <img src={imgsrc1} style={{ width: "300px", height: "250px" }} /> */}
            {isSecurityDataAvailable ? (
              <Plot
                data={[
                  {
                    type: 'bar',
                    x: ["Data Protection", "Access Control", "Compliance"],
                    y: [
                      dataProtectionScore,
                      accessControlScore,
                      complianceScore,
                    ],
                    marker: {
                      color: ['lightgreen', '#FFD580', '#FF5252', 'lightblue']
                    },
                  },
                ]}
                layout={{
                  borderRadius: '15%',
                  overflow: 'hidden',
                  height: 240,
                  width: 320,
                  xaxis: {
                    title: {
                      text: '',
                      font: {
                        size: 12,
                        weight: 'bold'
                      },
                      standoff: 10
                    },
                    type: 'category',
                    // tickangle: -45,
                    automargin: true,
                    tickfont: {
                      weight: 'bold',
                      size: 8
                    }
                  },
                  yaxis: {
                    title: {
                      text: "",
                      font: {
                        size: 12,
                        weight: 'bold'
                      },
                      standoff: 10
                    },
                    type: 'linear',
                    automargin: true,
                    tickfont: {
                      // weight: 'bold',
                      size: 12
                    },
                    tickformat: ',.0f',
                    separatethousands: true,
                    hoverformat: ',.2f'
                  },
                  title: {
                    text: "",
                    x: 0.5,
                    xref: 'paper',
                    font: {
                      size: 12,
                      weight: 'bold'
                    }
                  },
                  margin: { t: 50, b: 20, l: 40, r: 10 },
                  bargap: 0.5,
                  scene: {
                    camera: {
                      eye: { x: 1.5, y: 1.5, z: 1.2 }
                    }
                  },
                  transition: {
                    duration: 1000,
                    easing: 'cubic-in-out'
                  }
                }}
              />
            ) : (
              <div>Data is not available</div>
            )}
          </div>
        ) : (
          <div>
            <h5>Trust Center</h5>
            <p>For the Most Sensitive Matters</p>
          </div>
        );

      default:
        return <div>Select an option</div>;
    }
  };

  /* -------------------------------------------
      LEFT SECTION (depends on middle tab)
  ------------------------------------------- */
  const renderLeftContent = () => {
    if (!selectedMiddleTab) return renderDefaultView("left");

    /** PRODUCT TAB → Dynamic */
    if (selectedTopTab === "product") {
      const product =
        companyDetails?.products?.find(
          (p) => p.product_name === selectedMiddleTab
        );

      if (!product)
        return <div>No product details found</div>;

      return (
        <div>
          <h5>Process Coverage</h5>
          {/* <img src={imgsrc} style={{ width: "300px", height: "250px" }} /> */}

          <Plot
            data={[
              {
                type: 'bar',
                x: ["contact man", "compliance", "Legal Operation"],
                y: [126, 289, 231, 562],
                marker: {
                  color: ['lightgreen', '#FFD580', '#FF5252', 'lightblue']
                },
              },
            ]}
            layout={{
              borderRadius: '15%',
              overflow: 'hidden',
              height: 240,
              width: 320,
              xaxis: {
                title: {
                  text: '',
                  font: {
                    size: 12,
                    weight: 'bold'
                  },
                  standoff: 10
                },
                type: 'category',
                // tickangle: -45,
                automargin: true,
                tickfont: {
                  weight: 'bold',
                  size: 8
                }
              },
              yaxis: {
                title: {
                  text: "",
                  font: {
                    size: 12,
                    weight: 'bold'
                  },
                  standoff: 10
                },
                type: 'linear',
                automargin: true,
                tickfont: {
                  // weight: 'bold',
                  size: 12
                },
                tickformat: ',.0f',
                separatethousands: true,
                hoverformat: ',.0f'
              },
              title: {
                text: "",
                x: 0.5,
                xref: 'paper',
                font: {
                  size: 12,
                  weight: 'bold'
                }
              },
              margin: { t: 50, b: 20, l: 100, r: 10 },
              bargap: 0.5,
              scene: {
                camera: {
                  eye: { x: 1.5, y: 1.5, z: 1.2 }
                }
              },
              transition: {
                duration: 1000,
                easing: 'cubic-in-out'
              }
            }}
          />
        </div>
      );
    }

    /** ABOUT → Key Members */
    if (selectedMiddleTab === "Key Members") {
      const employeeData =
        companyDetails?.company_details?.company_fundamentals.employee_count;

      const values = employeeData?.values || [];

      // Check if values are suitable for graph
      const isGraphData = Array.isArray(values) && values.length > 0 && values.every(
        item => item && typeof item.count !== 'undefined' && typeof item.date !== 'undefined'
      );

      // Advanced Sorting & Sampling Logic
      let x_values = [];
      let y_values = [];

      if (isGraphData) {
        const sortedValues = [...values].sort((a, b) => {
          return new Date(a.date) - new Date(b.date);
        });

        // ADVANCED: Dynamic Plot Selection (Smart Sampling)
        // If there's too much data, we show key milestones to highlight growth trends clearly
        const sampledData = sortedValues.length > 10
          ? sortedValues.filter((_, idx) => idx % 2 === 0 || idx === sortedValues.length - 1)
          : sortedValues;

        x_values = sampledData.map(item => item.date);
        y_values = sampledData.map(item => Number(item.count));
      }

      return (
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          <h3 style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "10px" }}>Employee Growth Trend</h3>

          {isGraphData ? (
            <div style={{ width: "100%", height: "240px" }}>
              <Plot
                data={[
                  {
                    type: "scatter",
                    mode: "lines+markers",
                    x: x_values,
                    y: y_values,
                    // Advanced Visuals: Spline makes the "Growth" look smoother
                    line: { shape: "spline", smoothing: 1.3, width: 3, color: "#2563eb" },
                    marker: { size: 6, color: "#1e40af" },
                    // Area fill to emphasize the "Volume" of growth
                    fill: 'tozeroy',
                    fillcolor: 'rgba(37, 99, 235, 0.05)',
                  },
                ]}
                layout={{
                  autosize: true,
                  height: 220,
                  width: 320, // Fits the "Certain Box"
                  xaxis: {
                    type: "category",
                    automargin: true,
                    tickfont: { size: 9 },
                    gridcolor: "#f1f5f9",
                    showgrid: false
                  },
                  yaxis: {
                    // DYNAMIC SCALING: No fixed 0-50 limit
                    autorange: true,
                    fixedrange: false, // Allows user to move/drag
                    type: "linear",
                    automargin: true,
                    tickfont: { size: 10 },
                    tickformat: ",.0f",
                    gridcolor: "#f1f5f9",
                    zeroline: false
                  },
                  margin: { t: 10, b: 10, l: 35, r: 15 },
                  paper_bgcolor: "rgba(0,0,0,0)",
                  plot_bgcolor: "rgba(0,0,0,0)",
                  hovermode: "closest"
                }}
                config={{
                  displayModeBar: false,
                  responsive: true,
                  // Allows user to move/drag the graph to see hidden parts
                  scrollZoom: true
                }}
                style={{ width: "100%", height: "100%" }}
              />
            </div>
          ) : (
            <div style={{ padding: "20px", textAlign: "left" }}>
              <p style={{ color: "#94a3b8", fontSize: "13px" }}>
                {values.length === 0 ? "Data is unavailable for this company" : "Viewing list format:"}
              </p>
              {Array.isArray(values) && values.map((val, idx) => (
                <p key={idx} style={{ fontSize: "12px", margin: "4px 0" }}>
                  {typeof val === 'object' ? `${val.date}: ${val.count}` : val}
                </p>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (selectedMiddleTab === "Key Customers") {

      const revenueRaw =
        companyDetails?.company_details?.company_fundamentals?.yearly_revenue
          ?.values || [];

      const parseAmount = (val) => {
        if (!val) return 0;
        return parseFloat(val.replace(/[^0-9.]/g, ""));
      };

      const revenueMap = {};

      revenueRaw.forEach((item) => {
        const year = item?.date;
        const amount = parseAmount(item?.amount);

        if (!year) return;

        if (!revenueMap[year]) {
          revenueMap[year] = {
            total: 0,
            count: 0,
            displayValues: []
          };
        }

        revenueMap[year].total += amount;
        revenueMap[year].count += 1;
        revenueMap[year].displayValues.push(item?.amount);
      });

      const sortedYears = Object.keys(revenueMap).sort();

      const x_values = sortedYears;

      const y_values = sortedYears.map((year) => {
        const { total, count } = revenueMap[year];
        return count ? total / count : 0;
      });

      const avgDisplay = sortedYears.map((year) => {
        const { total, count, displayValues } = revenueMap[year];

        if (!count) return "0";

        const avg = (total / count).toFixed(2);

        const sampleValue = displayValues[0] || "";

        // dynamic currency
        const currencyMatch = sampleValue.match(/^[^\d]+/);
        const currency = currencyMatch ? currencyMatch[0] : "";

        // dynamic unit
        const unitMatch = sampleValue.match(/[A-Za-z]+$/);
        const unit = unitMatch ? unitMatch[0] : "";

        return `${currency}${avg}${unit}`;
      });
      return (
        <div>
          <h3>Revenue</h3>
          <Plot
            data={[
              {
                type: "bar",
                x: x_values,
                y: y_values,

                text: avgDisplay,
                textposition: "none",
                hovertemplate: "%{text}<extra></extra>",
                marker: {
                  color: ['lightgreen', '#FFD580', '#FF5252', 'lightblue']
                },
              },
            ]}
            layout={{
              borderRadius: '15%',
              overflow: 'hidden',
              height: 240,
              width: 300,
              xaxis: {
                title: {
                  text: '',
                  font: {
                    size: 12,
                    weight: 'bold'
                  },
                  standoff: 10
                },
                type: 'category',
                // tickangle: -45,
                automargin: true,
                tickfont: {
                  weight: 'bold',
                  size: 8
                }
              },
              yaxis: {
                title: {
                  text: "",
                  font: {
                    size: 12,
                    weight: 'bold'
                  },
                  standoff: 10
                },
                type: 'linear',
                automargin: true,
                tickfont: {
                  // weight: 'bold',
                  size: 12
                },
                separatethousands: true,
              },
              title: {
                text: "Revenue",
                x: 0.5,
                xref: 'paper',
                font: {
                  size: 12,
                  weight: 'bold'
                }
              },
              margin: { t: 50, b: 20, l: 100, r: 10 },
              bargap: 0.5,
              scene: {
                camera: {
                  eye: { x: 1.5, y: 1.5, z: 1.2 }
                }
              },
              transition: {
                duration: 1000,
                easing: 'cubic-in-out'
              }
            }}
          />
        </div>
      );
    }

    if (selectedMiddleTab === "Key Partners") {
      return (
        <div>
          <h3>Partner Reach</h3>
          <p>
          </p>
        </div>
      );
    }

    /** ABOUT → Regional Presence */
    if (selectedMiddleTab === "Regional Presence") {
      return (
        <div>
          <h3>Territory Expansion</h3>
          <MapView
            regions={
              companyDetails?.company_details?.market_presence
                ?.geographical_presence?.selected
            }
          />
        </div>
      );
    }

    if (selectedMiddleTab === "Data Protection") {
      return (
        <div>
          <h3>Security</h3>
          {isSecurityDataAvailable ? (
            <Plot
              data={[
                {
                  type: 'bar',
                  x: ["Data Protection", "Access Control", "Compliance"],
                  y: [
                    dataProtectionScore,
                    accessControlScore,
                    complianceScore,
                  ],
                  marker: {
                    color: ['lightgreen', '#FFD580', '#FF5252', 'lightblue']
                  },
                },
              ]}
              layout={{
                borderRadius: '15%',
                overflow: 'hidden',
                height: 240,
                width: 320,
                xaxis: {
                  title: {
                    text: '',
                    font: {
                      size: 12,
                      weight: 'bold'
                    },
                    standoff: 10
                  },
                  type: 'category',
                  // tickangle: -45,
                  automargin: true,
                  tickfont: {
                    weight: 'bold',
                    size: 8
                  }
                },
                yaxis: {
                  title: {
                    text: "",
                    font: {
                      size: 12,
                      weight: 'bold'
                    },
                    standoff: 10
                  },
                  type: 'linear',
                  automargin: true,
                  tickfont: {
                    // weight: 'bold',
                    size: 12
                  },
                  tickformat: ',.0f',
                  separatethousands: true,
                  hoverformat: ',.2f'
                },
                title: {
                  text: "",
                  x: 0.5,
                  xref: 'paper',
                  font: {
                    size: 12,
                    weight: 'bold'
                  }
                },
                margin: { t: 50, b: 20, l: 40, r: 10 },
                bargap: 0.5,
                scene: {
                  camera: {
                    eye: { x: 1.5, y: 1.5, z: 1.2 }
                  }
                },
                transition: {
                  duration: 1000,
                  easing: 'cubic-in-out'
                }
              }}
            />
          ) : (
            <div>No data available.</div>
          )}
        </div>
      );
    }

    if (selectedMiddleTab === "Access Control") {
      return (
        <div>
          <h3>Security</h3>
          {isSecurityDataAvailable ? (
            <Plot
              data={[
                {
                  type: 'bar',
                  x: ["Data Protection", "Access Control", "Compliance"],
                  y: [
                    dataProtectionScore,
                    accessControlScore,
                    complianceScore,
                  ],
                  marker: {
                    color: ['lightgreen', '#FFD580', '#FF5252', 'lightblue']
                  },
                },
              ]}
              layout={{
                borderRadius: '15%',
                overflow: 'hidden',
                height: 240,
                width: 320,
                xaxis: {
                  title: {
                    text: '',
                    font: {
                      size: 12,
                      weight: 'bold'
                    },
                    standoff: 10
                  },
                  type: 'category',
                  // tickangle: -45,
                  automargin: true,
                  tickfont: {
                    weight: 'bold',
                    size: 8
                  }
                },
                yaxis: {
                  title: {
                    text: "",
                    font: {
                      size: 12,
                      weight: 'bold'
                    },
                    standoff: 10
                  },
                  type: 'linear',
                  automargin: true,
                  tickfont: {
                    // weight: 'bold',
                    size: 12
                  },
                  tickformat: ',.0f',
                  separatethousands: true,
                  hoverformat: ',.2f'
                },
                title: {
                  text: "",
                  x: 0.5,
                  xref: 'paper',
                  font: {
                    size: 12,
                    weight: 'bold'
                  }
                },
                margin: { t: 50, b: 20, l: 40, r: 10 },
                bargap: 0.5,
                scene: {
                  camera: {
                    eye: { x: 1.5, y: 1.5, z: 1.2 }
                  }
                },
                transition: {
                  duration: 1000,
                  easing: 'cubic-in-out'
                }
              }}
            />
          ) : (
            <div>No data available.</div>
          )}
        </div>
      );
    }

    if (selectedMiddleTab === "Compliance & Certs") {
      return (
        <div>
          <h3>Security</h3>
          {isSecurityDataAvailable ? (
            <Plot
              data={[
                {
                  type: 'bar',
                  x: ["Data Protection", "Access Control", "Compliance"],
                  y: [
                    dataProtectionScore,
                    accessControlScore,
                    complianceScore,
                  ],
                  marker: {
                    color: ['lightgreen', '#FFD580', '#FF5252', 'lightblue']
                  },
                },
              ]}
              layout={{
                borderRadius: '15%',
                overflow: 'hidden',
                height: 240,
                width: 320,
                xaxis: {
                  title: {
                    text: '',
                    font: {
                      size: 12,
                      weight: 'bold'
                    },
                    standoff: 10
                  },
                  type: 'category',
                  // tickangle: -45,
                  automargin: true,
                  tickfont: {
                    weight: 'bold',
                    size: 8
                  }
                },
                yaxis: {
                  title: {
                    text: "",
                    font: {
                      size: 12,
                      weight: 'bold'
                    },
                    standoff: 10
                  },
                  type: 'linear',
                  automargin: true,
                  tickfont: {
                    // weight: 'bold',
                    size: 12
                  },
                  tickformat: ',.0f',
                  separatethousands: true,
                  hoverformat: ',.2f'
                },
                title: {
                  text: "",
                  x: 0.5,
                  xref: 'paper',
                  font: {
                    size: 12,
                    weight: 'bold'
                  }
                },
                margin: { t: 50, b: 20, l: 40, r: 10 },
                bargap: 0.5,
                scene: {
                  camera: {
                    eye: { x: 1.5, y: 1.5, z: 1.2 }
                  }
                },
                transition: {
                  duration: 1000,
                  easing: 'cubic-in-out'
                }
              }}
            />
          ) : (
            <div>No data available.</div>
          )}
        </div>
      );
    }

    return (
      <div className="left-card">
        Left section for: {selectedMiddleTab}
      </div>
    );
  };

  /* -------------------------------------------
      RIGHT SECTION (depends on middle tab)
  ------------------------------------------- */
  const renderRightContent = () => {
    if (!selectedMiddleTab && selectedTopTab !== "similar") {
      return renderDefaultView("right");
    }


    /** PRODUCT TAB → Dynamic */
    if (selectedTopTab === "product") {
      const product =
        companyDetails?.products?.find(
          (p) => p.product_name === selectedMiddleTab
        );

      return (
        <div>
          <p>{product?.product_description} </p>
          {/* <ul>
            {product?.features?.map((f, i) => (
              <li key={i}>{f}</li>
            )) || <li>No features</li>}
          </ul> */}
        </div>
      );
    }

    /** ABOUT → Key Members */
    if (selectedMiddleTab === "Key Members") {
      const members =
        companyDetails?.company_details?.company_fundamentals?.key_leadership?.value || [];

      if (!members.length) {
        return (
          <p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>
            No leadership information available.
          </p>
        );
      }

      return (
        <div
          className="right-members-container"
          style={{
            display: "grid",
            // 2 columns of equal width
            gridTemplateColumns: "repeat(2, 1fr)",
            // 2 rows of equal height to consume the full vertical space
            gridTemplateRows: "repeat(2, 1fr)",
            gap: "12px",
            width: "100%",
            // This ensures the grid fills the parent tab's height completely
            height: "100%",
            minHeight: "600px", // Forces the square-ish shape in the tab area
            boxSizing: "border-box"
          }}
        >
          {members.slice(0, 4).map((member, index) => (
            <div
              key={index}
              className="member-card"
              style={{
                borderRadius: "12px",
                border: "1px solid #eef2f6",
                background: "#fff",
                boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                display: "flex",
                flexDirection: "column",
                textAlign: "left",
                overflow: "hidden",
                // Removes internal margins to maximize space consumption
                margin: 0
              }}
            >
              {/* Header Section */}
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", backgroundColor: "#f8fafc" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "700", fontSize: "16px", color: "#1e293b", lineHeight: "1.2" }}>
                      {member.name}
                    </div>
                    <div style={{ color: "#2563eb", fontSize: "13px", fontWeight: "600", marginTop: "2px" }}>
                      {member.position}
                    </div>
                  </div>

                  {member.linkedin_url && member.linkedin_url !== "cannot_found" && (
                    <LinkedInIcon
                      sx={{
                        color: "#0A66C2",
                        cursor: "pointer",
                        fontSize: 22,
                        "&:hover": { color: "#004182" }
                      }}
                      onClick={() => window.open(member.linkedin_url, "_blank")}
                    />
                  )}
                </div>
              </div>

              {/* Background Content - Fills the rest of the card height */}
              <div
                style={{
                  padding: "16px 20px",
                  overflowY: "auto",
                  flexGrow: 1,
                  backgroundColor: "#fff"
                }}
              >
                <span style={{ fontSize: "10px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", display: "block", marginBottom: "4px" }}>
                  Background
                </span>
                <div
                  style={{
                    fontSize: "13px",
                    lineHeight: "1.5",
                    color: "#475569"
                  }}
                >
                  {member.background}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    /** ABOUT → Key Customers */
    if (selectedMiddleTab === "Key Customers") {
      return (
        <ul>
          {companyDetails?.company_details?.market_presence
            ?.top_marquee_customers?.values?.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
        </ul>
      );
    }


    /** ABOUT → Key Partners */
    if (selectedMiddleTab === "Key Partners") {
      const partners =
        companyDetails?.company_details?.market_presence.recent_partnerships.values || [];

      if (!Array.isArray(partners) || partners.length === 0) {
        return (
          <p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>
            No partnership data available.
          </p>
        );
      }

      return (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", width: "20%" }}>
                  Partner
                </th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", width: "20%" }}>
                  Partnership Type
                </th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Description
                </th>
              </tr>
            </thead>

            <tbody>
              {partners.map((item, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td
                    style={{
                      padding: "20px 24px",
                      verticalAlign: "top",
                      color: "#1e293b",
                      fontSize: "14px",
                      fontWeight: "600",
                      borderBottom: "1px solid #f1f5f9",
                      textAlign: "left",
                    }}
                  >
                    {item?.partner_name || "N/A"}
                  </td>
                  <td
                    style={{
                      padding: "20px 24px",
                      verticalAlign: "top",
                      color: "#2563eb", // Blue accent for type
                      fontSize: "13px",
                      fontWeight: "600",
                      borderBottom: "1px solid #f1f5f9",
                      textAlign: "left",
                    }}
                  >
                    {item?.partnership_type || "N/A"}
                  </td>
                  <td
                    style={{
                      padding: "20px 24px",
                      verticalAlign: "top",
                      color: "#334155",
                      fontSize: "14px",
                      lineHeight: "1.6",
                      borderBottom: "1px solid #f1f5f9",
                      textAlign: "left",
                    }}
                  >
                    {item?.description || "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedMiddleTab === "Regional Presence") {
      const headquarters =
        companyDetails?.company_details?.about_company?.headquarters?.value;

      return (
        <div className="right-headquarters">
          <h4>Headquarters</h4>

          {headquarters ? (
            <p>{headquarters}</p>
          ) : (
            <p>No headquarters information available</p>
          )}
        </div>
      );
    }

    if (selectedMiddleTab === "Pricing") {
      return (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #000" }}>Product Name</th>
              <th style={{ border: "1px solid #000" }}>Field</th>
              <th style={{ border: "1px solid #000" }}>Value</th>
            </tr>
          </thead>

          <tbody>
            {companyDetails?.products?.map((productItem, pIndex) => {
              const pricingDetails = productItem?.gtm?.pricing_details?.value || [];
              const pricingType = productItem?.gtm?.pricing_type?.selected || "N/A";
              const costTransparency = productItem?.gtm?.cost_transparency?.selected || "N/A";
              const pricingFlexibility = productItem?.gtm?.pricing_flexibility?.values || "N/A";

              return (
                <React.Fragment key={pIndex}>
                  {/* 1: Product Detail Row */}
                  <tr>
                    {/* Product Name shown ONCE with rowspan of 3 */}
                    <td
                      rowSpan={4}
                      style={{
                        border: "1px solid #000",
                        padding: "10px",
                        verticalAlign: "top",
                        width: "30%",
                      }}
                    >
                      {productItem?.product_name}
                    </td>

                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      Pricing Type
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {pricingType}
                    </td>
                  </tr>

                  {/* 2: Pricing Type Row */}
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      Cost Transparency
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {costTransparency}
                    </td>
                  </tr>

                  {/* 3: Cost Transparency Row */}
                  <tr>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      Pricing Flexibility
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {pricingFlexibility}
                    </td>
                  </tr>

                  <tr>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      Pricing Details
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {pricingDetails}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      );
    }

    if (selectedMiddleTab === "Data Protection") {
      const products = Array.isArray(companyDetails?.products)
        ? companyDetails.products
        : [];

      const rows = products
        .map((product) => {
          const summary =
            product?.security_governance?.data_protection?.summary?.value;

          if (!summary) return null;

          return {
            productName: product.product_name,
            summary,
          };
        })
        .filter(Boolean);

      if (!rows.length) {
        return <p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No Data Protection data available.</p>;
      }

      return (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", width: "25%" }}>
                  Product
                </th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Summary
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td
                    style={{
                      padding: "20px 24px",
                      verticalAlign: "top",
                      color: "#1e293b",
                      fontSize: "14px",
                      fontWeight: "600",
                      borderBottom: "1px solid #f1f5f9",
                      textAlign: "left",
                    }}
                  >
                    {row.productName}
                  </td>

                  <td
                    style={{
                      padding: "20px 24px",
                      verticalAlign: "top",
                      color: "#334155",
                      fontSize: "14px",
                      lineHeight: "1.6",
                      borderBottom: "1px solid #f1f5f9",
                      textAlign: "left",
                    }}
                  >
                    {row.summary}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedMiddleTab === "Access Control") {
      const products = companyDetails?.products || [];

      if (!Array.isArray(products) || products.length === 0) {
        return <p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No products available.</p>;
      }

      return (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", width: "25%" }}>
                  Product
                </th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Summary
                </th>
              </tr>
            </thead>

            <tbody>
              {products.map((product, index) => {
                const summary =
                  product?.security_governance?.access_controls?.summary?.value;

                return (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                    <td
                      style={{
                        padding: "20px 24px",
                        verticalAlign: "top",
                        color: "#1e293b",
                        fontSize: "14px",
                        fontWeight: "600",
                        borderBottom: "1px solid #f1f5f9",
                        textAlign: "left",
                      }}
                    >
                      {product?.product_name || "Unknown Product"}
                    </td>

                    <td
                      style={{
                        padding: "20px 24px",
                        verticalAlign: "top",
                        color: "#334155",
                        fontSize: "14px",
                        lineHeight: "1.6",
                        borderBottom: "1px solid #f1f5f9",
                        textAlign: "left",
                      }}
                    >
                      {summary || "No access control summary available."}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedMiddleTab === "Compliance & Certs") {
      const products = companyDetails?.products || [];

      if (!Array.isArray(products) || products.length === 0) {
        return (
          <p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>
            No products available.
          </p>
        );
      }

      return (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px", width: "25%" }}>
                  Product
                </th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Summary
                </th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => {
                const summary =
                  product?.security_governance?.compliance_certifications?.summary?.value;

                return (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                    <td
                      style={{
                        padding: "20px 24px",
                        verticalAlign: "top",
                        color: "#1e293b",
                        fontSize: "14px",
                        fontWeight: "600",
                        borderBottom: "1px solid #f1f5f9",
                        textAlign: "left",
                      }}
                    >
                      {product?.product_name || "Unknown Product"}
                    </td>

                    <td
                      style={{
                        padding: "20px 24px",
                        verticalAlign: "top",
                        color: "#334155",
                        fontSize: "14px",
                        lineHeight: "1.6",
                        borderBottom: "1px solid #f1f5f9",
                        textAlign: "left",
                      }}
                    >
                      {summary || "No compliance summary available."}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }


    //Analyst Speak tabs
    if (selectedMiddleTab === "Management Consulting") {
      const data = companyDetails?.company_details?.media_buzz?.analyst_speak?.management_consulting || [];

      return data.length ? (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Summary</th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", width: "120px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Link</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td style={{ padding: "20px 24px", verticalAlign: "top", color: "#334155", fontSize: "14px", lineHeight: "1.6", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>{item?.summary?.value}</td>
                  <td style={{ padding: "20px 24px", textAlign: "left", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                      {item?.link && item.link !== "cannot_found" && (
                        <div style={{ display: "inline-flex", padding: "8px", borderRadius: "8px", backgroundColor: "#eff6ff" }}>
                          <LaunchIcon sx={{ cursor: "pointer", color: "#2563eb", fontSize: "18px", transition: "0.2s", "&:hover": { transform: "scale(1.15)", color: "#1e40af" } }} onClick={() => window.open(item.link, "_blank")} />
                        </div>
                      )}
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#94a3b8", display: "block", marginTop: "4px" }}>{item?.source_name || "N/A"}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (<p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No management consulting commentary available.</p>);
    }

    if (selectedMiddleTab === "Tech Research") {
      const data = companyDetails?.company_details?.media_buzz?.analyst_speak?.tech_research || [];

      return data.length ? (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Summary</th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", width: "120px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Link</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td style={{ padding: "20px 24px", verticalAlign: "top", color: "#334155", fontSize: "14px", lineHeight: "1.6", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>{item?.summary?.value}</td>
                  <td style={{ padding: "20px 24px", textAlign: "left", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                      {item?.link && item.link !== "cannot_found" && (
                        <div style={{ display: "inline-flex", padding: "8px", borderRadius: "8px", backgroundColor: "#eff6ff" }}>
                          <LaunchIcon sx={{ cursor: "pointer", color: "#2563eb", fontSize: "18px", transition: "0.2s", "&:hover": { transform: "scale(1.15)", color: "#1e40af" } }} onClick={() => window.open(item.link, "_blank")} />
                        </div>
                      )}
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#94a3b8", display: "block", marginTop: "4px" }}>{item?.source_name || "N/A"}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (<p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No tech research coverage available.</p>);
    }

    if (selectedMiddleTab === "Media & Academic") {
      const data = companyDetails?.company_details?.media_buzz?.analyst_speak?.media_and_academic || [];

      return data.length ? (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Summary</th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", width: "120px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Link</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td style={{ padding: "20px 24px", verticalAlign: "top", color: "#334155", fontSize: "14px", lineHeight: "1.6", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>{item?.summary?.value}</td>
                  <td style={{ padding: "20px 24px", textAlign: "left", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                      {item?.link && (
                        <div style={{ display: "inline-flex", padding: "8px", borderRadius: "8px", backgroundColor: "#eff6ff" }}>
                          <LaunchIcon sx={{ cursor: "pointer", color: "#2563eb", fontSize: "18px", transition: "0.2s", "&:hover": { transform: "scale(1.15)", color: "#1e40af" } }} onClick={() => window.open(item.link, "_blank")} />
                        </div>
                      )}
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#94a3b8", display: "block", marginTop: "4px" }}>{item?.source_name || "N/A"}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (<p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No media or academic coverage available.</p>);
    }

    if (selectedMiddleTab === "Thought Leaders") {
      const data = companyDetails?.company_details?.media_buzz?.analyst_speak?.thought_leaders || [];

      return data.length ? (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Summary</th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", width: "120px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Link</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td style={{ padding: "20px 24px", verticalAlign: "top", color: "#334155", fontSize: "14px", lineHeight: "1.6", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>{item?.summary?.value}</td>
                  <td style={{ padding: "20px 24px", textAlign: "left", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                      {item?.link && (
                        <div style={{ display: "inline-flex", padding: "8px", borderRadius: "8px", backgroundColor: "#eff6ff" }}>
                          <LaunchIcon sx={{ cursor: "pointer", color: "#2563eb", fontSize: "18px", transition: "0.2s", "&:hover": { transform: "scale(1.15)", color: "#1e40af" } }} onClick={() => window.open(item.link, "_blank")} />
                        </div>
                      )}
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#94a3b8", display: "block", marginTop: "4px" }}>{item?.source_name || "N/A"}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (<p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No thought leader commentary available.</p>);
    }

    if (selectedMiddleTab === "VC") {
      const data = companyDetails?.company_details?.media_buzz?.analyst_speak?.vc || [];

      return data.length ? (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Summary</th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", width: "120px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>Link</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td style={{ padding: "20px 24px", verticalAlign: "top", color: "#334155", fontSize: "14px", lineHeight: "1.6", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>{item?.summary?.value}</td>
                  <td style={{ padding: "20px 24px", textAlign: "left", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
                      {item?.link && (
                        <div style={{ display: "inline-flex", padding: "8px", borderRadius: "8px", backgroundColor: "#eff6ff" }}>
                          <LaunchIcon sx={{ cursor: "pointer", color: "#2563eb", fontSize: "18px", transition: "0.2s", "&:hover": { transform: "scale(1.15)", color: "#1e40af" } }} onClick={() => window.open(item.link, "_blank")} />
                        </div>
                      )}
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#94a3b8", display: "block", marginTop: "4px" }}>{item?.source_name || "N/A"}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (<p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No VC commentary available.</p>);
    }


    //Recent News tab
    /** NEWS → Dynamic Rendering */
    if (selectedMiddleTab === "Product Related") {
      const data =
        companyDetails?.company_details?.media_buzz?.recent_news?.product_related || [];

      return data.length ? (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Summary
                </th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", width: "100px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Link
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td style={{ padding: "20px 24px", verticalAlign: "top", color: "#334155", fontSize: "14px", lineHeight: "1.6", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>
                    {item?.headline}
                  </td>
                  <td style={{ padding: "20px 24px", textAlign: "left", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9" }}>
                    {item?.link && (
                      <div style={{ display: "inline-flex", padding: "8px", borderRadius: "8px", backgroundColor: "#eff6ff" }}>
                        <LaunchIcon
                          sx={{
                            cursor: "pointer",
                            color: "#2563eb",
                            fontSize: "18px",
                            transition: "0.2s",
                            "&:hover": { transform: "scale(1.15)", color: "#1e40af" }
                          }}
                          onClick={() => window.open(item.link, "_blank")}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No product-related news available.</p>
      );
    }

    if (selectedMiddleTab === "Partnerships & Customers") {
      const data =
        companyDetails?.company_details?.media_buzz?.recent_news?.partnerships_and_customers || [];

      return data.length ? (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Summary
                </th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", width: "100px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Link
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td style={{ padding: "20px 24px", verticalAlign: "top", color: "#334155", fontSize: "14px", lineHeight: "1.6", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>
                    {item?.headline}
                  </td>
                  <td style={{ padding: "20px 24px", textAlign: "left", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9" }}>
                    {item?.link && (
                      <div style={{ display: "inline-flex", padding: "8px", borderRadius: "8px", backgroundColor: "#eff6ff" }}>
                        <LaunchIcon
                          sx={{
                            cursor: "pointer",
                            color: "#2563eb",
                            fontSize: "18px",
                            transition: "0.2s",
                            "&:hover": { transform: "scale(1.15)", color: "#1e40af" }
                          }}
                          onClick={() => window.open(item.link, "_blank")}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No partnership news available.</p>
      );
    }

    if (selectedMiddleTab === "Expansion & Hiring") {
      const data =
        companyDetails?.company_details?.media_buzz?.recent_news?.expansion_and_hiring || [];

      return data.length ? (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Summary
                </th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", width: "100px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Link
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td style={{ padding: "20px 24px", verticalAlign: "top", color: "#334155", fontSize: "14px", lineHeight: "1.6", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>
                    {item?.headline}
                  </td>
                  <td style={{ padding: "20px 24px", textAlign: "left", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9" }}>
                    {item?.link && (
                      <div style={{ display: "inline-flex", padding: "8px", borderRadius: "8px", backgroundColor: "#eff6ff" }}>
                        <LaunchIcon
                          sx={{
                            cursor: "pointer",
                            color: "#2563eb",
                            fontSize: "18px",
                            transition: "0.2s",
                            "&:hover": { transform: "scale(1.15)", color: "#1e40af" }
                          }}
                          onClick={() => window.open(item.link, "_blank")}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No expansion or hiring news available.</p>
      );
    }

    if (selectedMiddleTab === "Media Coverage & Events") {
      const data =
        companyDetails?.company_details?.media_buzz?.recent_news?.media_coverage_and_events || [];

      return data.length ? (
        <div style={{ borderRadius: "12px", border: "1px solid #eef2f6", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Summary
                </th>
                <th style={{ borderBottom: "2px solid #f1f5f9", padding: "16px 24px", width: "100px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  Link
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, i) => (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#fbfcfd" }}>
                  <td style={{ padding: "20px 24px", verticalAlign: "top", color: "#334155", fontSize: "14px", lineHeight: "1.6", borderBottom: "1px solid #f1f5f9", textAlign: "left" }}>
                    {item?.headline}
                  </td>
                  <td style={{ padding: "20px 24px", textAlign: "left", verticalAlign: "middle", borderBottom: "1px solid #f1f5f9" }}>
                    {item?.link && (
                      <div style={{ display: "inline-flex", padding: "8px", borderRadius: "8px", backgroundColor: "#eff6ff" }}>
                        <LaunchIcon
                          sx={{
                            cursor: "pointer",
                            color: "#2563eb",
                            fontSize: "18px",
                            transition: "0.2s",
                            "&:hover": { transform: "scale(1.15)", color: "#1e40af" }
                          }}
                          onClick={() => window.open(item.link, "_blank")}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ textAlign: "left", color: "#94a3b8", padding: "40px", fontSize: "14px" }}>No media-related news available.</p>
      );
    }

    /* -------------------------------------------
       SIMILAR VENDORS → SCORE COMPARISON (RIGHT)
    ------------------------------------------- */
    if (selectedTopTab === "similar") {
      const currentCompanyScore = scoringResult?.total_score;

      // Defensive check
      if (!Array.isArray(domainDetails) || !currentCompanyScore) {
        return (
          <p style={{ color: "#94a3b8", padding: "20px" }}>
            Score comparison data not available.
          </p>
        );
      }

      // Exclude current company & sort others by score
      const topCompetitors = domainDetails
        .filter(
          (c) =>
            c?.id !== companyDetails?._id &&
            typeof c?.scoringResult?.total_score === "number"
        )
        .sort(
          (a, b) =>
            b.scoringResult.total_score - a.scoringResult.total_score
        )
        .slice(0, 3);

      return (
        <div style={{ padding: "20px" }}>
          <h4 style={{ marginBottom: "12px" }}>
            Score Comparison
          </h4>

          {/* Current Company */}
          <div
            style={{
              padding: "12px",
              borderRadius: "8px",
              background: "#eff6ff",
              marginBottom: "12px",
            }}
          >
            <strong>{companyDetails?.name}</strong>
            <div>Total Score: {currentCompanyScore}</div>
          </div>

          {/* Top 3 Similar Vendors */}
          {topCompetitors.map((vendor, index) => (
            <div
              key={vendor.id}
              style={{
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                marginBottom: "8px",
              }}
            >
              <strong>
                #{index + 1} {vendor.name}
              </strong>
              <div>
                Total Score: {vendor.scoringResult.total_score}
              </div>
            </div>
          ))}
        </div>
      );
    }


    return (
      <div className="right-placeholder">
        Right content for {selectedMiddleTab}
      </div>
    );
  };

  return (
    <div className="company-info-container">
      <div className="top-tabs">
        {topTabs.map((tab) => (
          <div
            key={tab.id}
            className={`top-tab ${selectedTopTab === tab.id ? "active" : ""
              }`}
            onClick={() => setSelectedTopTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <div className="info-layout">
        <div className="left-section">{renderLeftContent()}</div>

        {selectedTopTab !== "similar" && (
          <div className="middle-section">
            {middleTabs.map((item) => (
              <div
                key={item}
                className={`middle-item ${selectedMiddleTab === item ? "active" : ""}`}
                onClick={() => setSelectedMiddleTab(item)}
              >
                {item}
              </div>
            ))}
          </div>
        )}

        <div className="right-section">{renderRightContent()}</div>
      </div>
    </div>
  );
};

export default CompanyInfoPage;
